import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { es } from '@/i18n/es';
import { routes } from '@/lib/routes';
import { useIsAdmin, useSessionStatus } from '@/store/sessionStore';
import { useModerationQueue } from '@/hooks/useModerationQueue';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Sólo con sesión iniciada. */
  private?: boolean;
  adminOnly?: boolean;
  /** Cantidad pendiente. Cero o ausente no dibuja nada. */
  badge?: number;
}

/**
 * Punto rojo con el número de cosas pendientes.
 *
 * Chico y sin caja: en una barra de navegación lo que tiene que llamar la
 * atención es que HAY algo, no cuánto. El número exacto está adentro.
 */
function Badge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;

  return (
    <span
      className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-pill bg-danger px-1.5 py-0.5 font-mono text-2xs font-medium text-white"
      aria-label={label}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function useNavItems(): NavItem[] {
  const status = useSessionStatus();
  const isAdmin = useIsAdmin();
  const moderation = useModerationQueue();
  const notifications = useUnreadNotifications();
  const signedIn = status === 'ready';

  return [
    { to: routes.home, label: es.nav.home, icon: <HomeIcon /> },
    { to: routes.search, label: es.nav.search, icon: <SearchIcon /> },
    { to: routes.art, label: es.art.title, icon: <ArtIcon /> },
    { to: routes.board, label: es.board.title, icon: <BoardIcon /> },
    { to: routes.library, label: es.nav.library, icon: <LibraryIcon />, private: true },
    { to: routes.dashboard, label: es.nav.write, icon: <PenIcon />, private: true },
    {
      to: routes.notifications,
      label: es.nav.notifications,
      icon: <BellIcon />,
      private: true,
      // Faltaba, y era el bug: las notificaciones llegaban bien pero no había
      // ningún indicador, así que enterarse exigía entrar a la pantalla a
      // probar suerte.
      badge: notifications.unread,
    },
    {
      to: routes.admin,
      label: es.nav.admin,
      icon: <ShieldIcon />,
      adminOnly: true,
      badge: moderation.total,
    },
  ].filter((item) => {
    if (item.adminOnly) return isAdmin;
    if (item.private) return signedIn;
    return true;
  });
}

/** Barra inferior en mobile. Oculta en desktop, donde manda la lateral. */
export function BottomNav() {
  const items = useNavItems();
  return (
    <nav
      aria-label={es.nav.menu}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-paper/95 backdrop-blur md:hidden"
    >
      <ul className="flex items-stretch pb-[env(safe-area-inset-bottom)]">
        {items.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              end={item.to === routes.home}
              className={({ isActive }) =>
                cn(
                  'flex h-[var(--bottom-nav-height)] flex-col items-center justify-center gap-1 text-2xs',
                  isActive ? 'font-medium text-accent' : 'text-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/* En la barra de abajo no hay lugar para un número: el
                      punto va pegado al ícono y alcanza con que se vea. */}
                  <span className="relative">
                    {item.icon}
                    {(item.badge ?? 0) > 0 && (
                      <span
                        className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-danger"
                        aria-label={es.a11y.pending(item.badge ?? 0)}
                      />
                    )}
                  </span>
                  <span>{item.label}</span>
                  {isActive && <span className="sr-only">{es.a11y.currentPage}</span>}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Navegación lateral en desktop. */
export function SideNav() {
  const items = useNavItems();
  return (
    <nav aria-label={es.nav.menu} className="hidden w-52 shrink-0 md:block">
      <ul className="sticky top-[calc(var(--header-height)+1rem)] flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.to === routes.home}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-pill px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-accent-soft font-medium text-accent'
                    : 'text-muted hover:bg-surface-alt hover:text-ink',
                )
              }
            >
              {item.icon}
              {item.label}
              <Badge count={item.badge ?? 0} label={es.a11y.pending(item.badge ?? 0)} />
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

const ICON = 'h-5 w-5';
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1v-9.5Z" {...STROKE} />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6.5" {...STROKE} />
      <path d="m16 16 4 4" {...STROKE} />
    </svg>
  );
}

function ArtIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M4 5h16v14H4z" {...STROKE} />
      <path d="m4 16 5-5 4 4 3-2 4 3" {...STROKE} />
      <circle cx="9" cy="9" r="1.4" {...STROKE} />
    </svg>
  );
}

function BoardIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M4 5h16v11H9l-4 3.5V16H4V5Z" {...STROKE} />
    </svg>
  );
}

function LibraryIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M5 4h6v16H5zM13 4h3l3 16h-3z" {...STROKE} />
    </svg>
  );
}

function PenIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M4 20h4L20 8a2.8 2.8 0 0 0-4-4L4 16v4Zm11-13 3 3" {...STROKE} />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6Zm4 9a2 2 0 0 0 4 0" {...STROKE} />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" className={ICON} aria-hidden="true" focusable="false">
      <path d="M12 3 5 6v6c0 4.4 3 7.7 7 9 4-1.3 7-4.6 7-9V6l-7-3Z" {...STROKE} />
    </svg>
  );
}
